import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import { BUILTIN_WORKER_PROFILES } from "@/data/workerProfiles";
import type { WorkerProfile } from "@/types/workflowEngine";

function mapRow(row: Record<string, unknown>): WorkerProfile {
  return {
    id: row.id as string,
    userId: (row.user_id as string) ?? null,
    name: row.name as string,
    description: (row.description as string) ?? null,
    preferredModel: (row.preferred_model as string) ?? null,
    fallbackModels: (row.fallback_models as string[]) ?? [],
    temperature: (row.temperature as number) ?? 0.7,
    maxTokens: (row.max_tokens as number) ?? null,
    reasoningMode: Boolean(row.reasoning_mode),
    creativity: (row.creativity as string) ?? "balanced",
    determinism: (row.determinism as string) ?? "balanced",
    speedTier: (row.speed_tier as string) ?? "balanced",
    budgetPolicy: (row.budget_policy as Record<string, unknown>) ?? {},
    contextWindowHint: (row.context_window_hint as string) ?? null,
    isBuiltin: Boolean(row.is_builtin),
  };
}

class WorkerProfilesService {
  listBuiltin(): WorkerProfile[] {
    return BUILTIN_WORKER_PROFILES.map((p, i) => ({
      ...p,
      id: `builtin-${i}`,
      userId: null,
    }));
  }

  async listAll(): Promise<WorkerProfile[]> {
    const builtins = this.listBuiltin();
    if (!isSupabaseConfigured) return builtins;
    const userId = await getCurrentUserId();
    if (!userId) return builtins;
    const { data } = await supabase.from("worker_profiles").select("*").or(`user_id.eq.${userId},is_builtin.eq.true`);
    const custom = (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
    return [...builtins, ...custom.filter((c) => !c.isBuiltin)];
  }

  async getById(id: string): Promise<WorkerProfile | null> {
    if (id.startsWith("builtin-")) {
      const idx = parseInt(id.replace("builtin-", ""), 10);
      const builtins = this.listBuiltin();
      return builtins[idx] ?? null;
    }
    if (!isSupabaseConfigured) return null;
    const { data } = await supabase.from("worker_profiles").select("*").eq("id", id).maybeSingle();
    return data ? mapRow(data as Record<string, unknown>) : null;
  }

  async create(profile: Omit<WorkerProfile, "id" | "isBuiltin">): Promise<WorkerProfile | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;
    const { data, error } = await supabase
      .from("worker_profiles")
      .insert({
        user_id: userId,
        name: profile.name,
        description: profile.description,
        preferred_model: profile.preferredModel,
        fallback_models: profile.fallbackModels,
        temperature: profile.temperature,
        max_tokens: profile.maxTokens,
        reasoning_mode: profile.reasoningMode,
        creativity: profile.creativity,
        determinism: profile.determinism,
        speed_tier: profile.speedTier,
        budget_policy: profile.budgetPolicy,
        context_window_hint: profile.contextWindowHint,
        is_builtin: false,
      })
      .select()
      .single();
    if (error || !data) return null;
    return mapRow(data as Record<string, unknown>);
  }
}

export const workerProfilesService = new WorkerProfilesService();
